<template>
  <div class="chamarServico-dialog">
    <PDrawer
      v-model="visibleDrawer"
      width="590px"
      :title="drawerTitle"
      @close="handleClose"
      @onClose="handleClose"
      @beforeClose="handleClose"
      :close-on-click-modal="false"
      small-title
    >
      <template #default>
        <div class="chamarServico-dialog__content">
          <PBannerBase type="warning">
            <template #title>
              <span style="font-size: 12px; font-weight: 400">
                Veja o histórico e resultados de tentativas de comunicação entre
                o F360 Finanças e seus sistemas conectados ( adquirentes ,PDVs,
                Nfe e Open Finance)
              </span>
            </template>
          </PBannerBase>

          <div class="chamarServico-dialog__content-form">
            <template v-if="apiSelecionada?.banner">
              <component
                :is="apiSelecionada.banner"
                style="margin-bottom: 16px"
                :webservice="webservice"
                :show="canShowRedeBanner"
              />
            </template>

            <component
              :is="formularioDinamico"
              :webservice="webservice"
              :empresasOpt="empresasOpt"
              :apiSelecionada="apiSelecionada"
              ref="formularioRef"
              @submit="handleBuscarNovamente"
            />
            <div
              class="chamarServico-dialog__content-processamento"
              v-if="
                showProcessamentoAsync?.TipoProcessamento ===
                'ProcessamentoAsync'
              "
            >
              <p class="chamarServico-dialog__content-processamento-title">
                Processamento em segundo plano
              </p>
              <p
                class="chamarServico-dialog__content-processamento-description"
              >
                Processamento está sendo executado em segundo plano.
              </p>
              <p
                class="chamarServico-dialog__content-processamento-description"
              >
                <template v-if="!hasSftpSelection">
                  Para ver a evolução do processamento, clique em
                  <span
                    class="chamarServico-dialog__content-processamento-highlight"
                  >
                    "Ver Detalhes" </span
                  >.
                </template>
                <template v-else>
                  Integrações SFTP podem levar até três horas para processar os
                  arquivos.
                </template>
              </p>
            </div>
            <div v-if="apiSelecionada?.intervaloData">
              <PBannerBase
                :error="false"
                height="auto"
                :inative="false"
                :isDefault="false"
                warning
              >
                <template #title>Atenção</template>

                O intervalo de data entre início e fim não deve ser supeior a
                {{ apiSelecionada?.intervaloData }} dias.
              </PBannerBase>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="chamarServico-dialog__footer">
          <PButton
            v-if="!hasSftpSelection"
            type="outline"
            @click="handleAbrirHistorico"
            :disabled="!showProcessamentoAsync?.serviceId"
            :loading="isLoading"
            >Ver detalhes</PButton
          >
          <PButton
            v-if="canSubmitBuscarNovamente"
            @click="handleBuscarNovamente"
            suffix-icon="sync_alt"
            :disabled="disableBuscaNovamento"
            :loading="isLoading"
            >Buscar Novamente</PButton
          >
        </div>
      </template>
    </PDrawer>

    <HistoricoIntegracaoDrawer
      v-if="showHistorico"
      :webservice="webservice ?? ({} as TWebservice)"
      :painelIntegracoesGateway="painelIntegracoesGateway"
      @close="showHistorico = false"
    />
  </div>
</template>

<script setup lang="ts">
import { diContainer } from "@/inversify.config";
import { PBannerBase, PButton, PDrawer } from "@f360-dev/f360-prisma";
import {
  type Component,
  computed,
  markRaw,
  onMounted,
  ref,
  shallowRef
} from "vue";
import {
  PainelDeIntegracaoesDi,
  PainelDeIntegracaoesGateway,
  TChamarServicoParams,
  TChamarServicoItemResponse,
  TChamarServicoResponse,
  TIntegrationType,
  TSelectedIntegration,
  TWebservice
} from "../../services/PainelDeIntegracaoesGateway";
import {
  CadastroDeIntegracaoDi,
  CadastroDeIntegracaoGateway
} from "../cadastro/services/CadastroDeIntegracaoGateway";
import useChamarServico from "./composables/useChamarServico";
import handleEmpresasOpt, {
  IEmpresasOpt
} from "@/core/utils/empresasOpt/handleEmpresasOpt";
import { EIntegracoesAPIEnum } from "../../../../@types/IntegracoesAPIEnum";
import { IToastNotification } from "@/core/composables/notification/types";
import { DITypes, EPluggyTypes } from "@/core/@types/types";
import HistoricoIntegracaoDrawer from "../historico/HistoricoIntegracaoDrawer.vue";
import FormularioIntegracaoBase from "./forms/FormularioIntegracaoBase.vue";
import { TAPIRules } from "../../../../@types/IntegracoesAPIRules";
import { IPluggyConnector } from "@/core/common/Pluggy/services/types";
import { supportsSubmitChamarServico } from "./composables/chamarServicoRequest";
import { IFinancasFeatureGateway } from "@/features/financas/services/ports/FinancasFeatureGateway";
import useAnalyticsIntegracoes, {
  AnalyticsIntegracoesHistoryOrigin,
  AnalyticsIntegracoesResult,
  TAnalyticsIntegracoesSearchAgainDate,
  formatAnalyticsIntegracoesDate
} from "../../../../analytics/useAnalyticsIntegracoes";

type TFormularioChamarServicoExpose = {
  values?: Record<string, unknown>;
  disabledSubmit?: boolean;
};

const isLoading = ref(false);
const canShowRedeBanner = ref(false);

const props = defineProps<{
  serviceId?: string;
  visibleDrawer: boolean;
  webservice?: TWebservice | undefined;
  webservices?: TWebservice[];
}>();

const visibleDrawer = ref(props.visibleDrawer || false);
const showHistorico = ref(false);
const formularioRef = ref<TFormularioChamarServicoExpose | null>(null);
const apiSelecionada = shallowRef<TAPIRules | undefined>(undefined);

const empresasOpt = ref<IEmpresasOpt[]>([]);

const showProcessamentoAsync = ref<TChamarServicoResponse | undefined>({
  serviceId: undefined,
  TipoProcessamento: undefined
});

const painelIntegracoesGateway = diContainer.get<PainelDeIntegracaoesGateway>(
  PainelDeIntegracaoesDi
);

const cadastroGateway = diContainer.get<CadastroDeIntegracaoGateway>(
  CadastroDeIntegracaoDi
);

const pluggyConnector = diContainer.get<IPluggyConnector>(
  EPluggyTypes.PLUGGY_CONNECTOR
);

const financasFeatureGateway = diContainer.get<IFinancasFeatureGateway>(
  DITypes.FINANCAS_FEATURES
);

const notification = diContainer.get<IToastNotification<string>>(
  DITypes.NOTIFICATION
);

const { handle: handleEmpresas } = handleEmpresasOpt();

const { chamarServico, handleAPI } = useChamarServico(
  painelIntegracoesGateway,
  pluggyConnector
);
const { sendSearchAgain, sendOpenHistory } = useAnalyticsIntegracoes();

const formularioDinamico = shallowRef<Component | null>(null);

const selectedWebservices = computed(() => {
  if (props.webservices?.length) return props.webservices;

  return props.webservice ? [props.webservice] : [];
});

const isMultipleMode = computed(() => selectedWebservices.value.length > 1);

const getIntegrationType = (webservice?: TWebservice): TIntegrationType => {
  const type = webservice?.tipo ?? webservice?.Tipo;

  if (typeof type === "string") {
    const normalizedType = type.trim().toUpperCase();

    if (normalizedType === "SFTP") return "SFTP";
    if (normalizedType === "WEBHOOK") return "Webhook";
  }

  return webservice?.IsSFTP ? "SFTP" : "API";
};

const hasSftpSelection = computed(() =>
  selectedWebservices.value.some(
    (selectedWebservice) => getIntegrationType(selectedWebservice) === "SFTP"
  )
);

const usesUnifiedSelection = computed(
  () => isMultipleMode.value || hasSftpSelection.value
);

const drawerTitle = computed(() => {
  if (isMultipleMode.value) {
    return `Buscar novamente: ${selectedWebservices.value.length} integrações`;
  }

  return `Buscar novamente: ${props.webservice?.integracao ?? ''}`;
});

const multipleSearchApiRules = computed<TAPIRules>(() => ({
  actionByRange: "/v1/WebserviceAPI/CallWebserviceByRangeDate",
  apiHash: EIntegracoesAPIEnum.None,
  api: "Integrações selecionadas",
  forcesRange: true,
  hideCompanyField: true,
  hideDateField: true,
  hidePeriodSelector: false
}));

const canSubmitBuscarNovamente = computed(() => {
  return supportsSubmitChamarServico(apiSelecionada.value);
});

const disableBuscaNovamento = computed(() => {
  return (
    !canSubmitBuscarNovamente.value ||
    (formularioRef.value?.disabledSubmit ?? false)
  );
});

const emit = defineEmits<{
  (e: "close", payload?: { executed?: boolean }): void;
  (e: "executed"): void;
}>();

const handleClose = (payload?: { executed?: boolean }) => {
  visibleDrawer.value = false;
  emit("close", payload);
};

const getIntegrationName = (webservice = props.webservice) =>
  webservice?.integracao?.toString() ||
  webservice?.Webservice?.toString() ||
  "";

const getWebserviceId = (webservice: TWebservice) =>
  webservice._id ?? webservice.id;

const getSelectedIntegration = (
  webservice: TWebservice
): TSelectedIntegration => {
  const tipo = getIntegrationType(webservice);
  const integration: TSelectedIntegration = {
    tipo,
    id: getWebserviceId(webservice)
  };

  if (tipo === "SFTP" && webservice.VanEntidade != null) {
    integration.entidade = Number(webservice.VanEntidade);
  }

  return integration;
};

const getHistoricoWebservice = () =>
  props.webservice ?? selectedWebservices.value[0];

const getHistoricoWebserviceId = () => {
  const historicoWebservice = getHistoricoWebservice();

  return historicoWebservice ? getWebserviceId(historicoWebservice) : undefined;
};

const getSearchDates = (
  values: Record<string, unknown>
): TAnalyticsIntegracoesSearchAgainDate => {
  const start = values.inicio ?? values.DataInicio;
  const end = values.fim ?? values.DataFim;

  if (start && end) {
    return {
      startDate: formatAnalyticsIntegracoesDate(start),
      endDate: formatAnalyticsIntegracoesDate(end)
    };
  }

  const date = values.date ?? values.Data;
  const parsedDate = formatAnalyticsIntegracoesDate(date);

  return {
    startDate: parsedDate,
    endDate: parsedDate
  };
};

const getResponseServiceId = (response?: {
  Id?: string;
  id?: string;
  ServiceId?: string;
  serviceId?: string;
}) =>
  response?.ServiceId ?? response?.serviceId ?? response?.Id ?? response?.id;

const getMultipleResult = (
  responses: Array<
    | {
        Result?: TChamarServicoResponse | boolean;
      }
    | undefined
  >
) => responses[0]?.Result as TChamarServicoResponse | undefined;

const getMultipleResultServiceIds = (
  responses?: TChamarServicoItemResponse[]
) => new Set((responses ?? []).map(getResponseServiceId).filter(Boolean));

const getResponseIntegrationKey = (response?: TChamarServicoItemResponse) => {
  const id = response?.Id ?? response?.id ?? getResponseServiceId(response);
  const tipo = response?.Tipo ?? response?.tipo;
  const entidade = response?.Entidade ?? response?.entidade;

  if (!id) return undefined;
  if (!tipo) return `ID:${id}`;

  if (String(tipo).toUpperCase() === "SFTP" && entidade != null) {
    return `SFTP:${entidade}`;
  }

  return `${String(tipo).toUpperCase()}:${id}:${entidade ?? ""}`;
};

const getSelectedIntegrationKey = (webservice: TWebservice) => {
  const integration = getSelectedIntegration(webservice);

  if (integration.tipo === "SFTP" && integration.entidade != null) {
    return `SFTP:${integration.entidade}`;
  }

  return `${integration.tipo.toUpperCase()}:${integration.id}:${integration.entidade ?? ""}`;
};

const getMultipleResultIntegrationKeys = (
  responses?: TChamarServicoItemResponse[]
) => new Set((responses ?? []).map(getResponseIntegrationKey).filter(Boolean));

const handleBuscarNovamente = async () => {
  if (!selectedWebservices.value.length || !canSubmitBuscarNovamente.value) return;

  const formValues = formularioRef.value?.values ?? {};
  const integrationName = isMultipleMode.value
    ? `${selectedWebservices.value.length} integrações`
    : getIntegrationName();
  const searchDates = getSearchDates(formValues);

  try {
    isLoading.value = true;

    const responses = usesUnifiedSelection.value
      ? [await chamarServicoParaWebservicesSelecionados(formValues).catch(() => {
          return {
            Result: false,
            Error: "Erro ao buscar novamente",
            Ok: false
          };
        })]
      : await Promise.all(
          selectedWebservices.value.map((selectedWebservice) =>
            chamarServicoParaWebservice(selectedWebservice, formValues).catch(() => {
              return {
                Result: false,
                Error: "Erro ao buscar novamente",
                Ok: false
              };
            })
          )
        );
    const multipleResult = usesUnifiedSelection.value
      ? getMultipleResult(responses)
      : undefined;
    const successResponses = responses.filter((response) => response?.Ok);
    const errorResponses = responses.filter((response) => response?.Ok !== true);
    const successCount = usesUnifiedSelection.value
      ? multipleResult?.QuantidadeProcessada ?? (responses[0]?.Ok ? selectedWebservices.value.length : 0)
      : successResponses.length;
    const errorCount = usesUnifiedSelection.value
      ? multipleResult?.QuantidadeComErro ?? (responses[0]?.Ok ? 0 : selectedWebservices.value.length)
      : errorResponses.length;
    const hasSuccess = successCount > 0;
    const hasError = errorCount > 0;
    const successfulServiceIds = getMultipleResultServiceIds(
      multipleResult?.Sucessos
    );
    const failedServiceIds = getMultipleResultServiceIds(multipleResult?.Erros);
    const successfulIntegrationKeys = getMultipleResultIntegrationKeys(
      multipleResult?.Sucessos
    );
    const failedIntegrationKeys = getMultipleResultIntegrationKeys(
      multipleResult?.Erros
    );

    selectedWebservices.value.forEach((selectedWebservice, index) => {
      const response = usesUnifiedSelection.value
        ? responses[0]
        : responses[index];
      const selectedWebserviceId = getWebserviceId(selectedWebservice);
      const hasStructuredMultipleResult =
        successfulServiceIds.size > 0 ||
        failedServiceIds.size > 0 ||
        successfulIntegrationKeys.size > 0 ||
        failedIntegrationKeys.size > 0;
      const selectedIntegrationKey =
        getSelectedIntegrationKey(selectedWebservice);
      const hasSuccessfulResult =
        successfulIntegrationKeys.has(selectedIntegrationKey) ||
        successfulIntegrationKeys.has(`ID:${selectedWebserviceId}`) ||
        successfulServiceIds.has(selectedWebserviceId);
      const searchResult = hasStructuredMultipleResult
        ? hasSuccessfulResult
          ? AnalyticsIntegracoesResult.Success
          : AnalyticsIntegracoesResult.Error
        : response?.Ok
          ? AnalyticsIntegracoesResult.Success
          : AnalyticsIntegracoesResult.Error;

      sendSearchAgain(
        getIntegrationName(selectedWebservice),
        searchResult,
        searchDates
      );
    });

    if (hasSuccess) {
      const result = (multipleResult ?? successResponses[0]?.Result) as
        | TChamarServicoResponse
        | undefined;

      emit("executed");

      if (hasSftpSelection.value) {
        showProcessamentoAsync.value = {
          serviceId: undefined,
          TipoProcessamento: "ProcessamentoAsync"
        };
      } else if (result?.TipoProcessamento === "ProcessamentoAsync") {
        showProcessamentoAsync.value = result;
        showProcessamentoAsync.value.serviceId =
          result?.serviceId ?? getHistoricoWebserviceId();
      } else if (isMultipleMode.value) {
        showProcessamentoAsync.value = {
          serviceId: getHistoricoWebserviceId(),
          TipoProcessamento: "ProcessamentoAsync"
        };
      } else {
        handleClose();
      }

      notification.showNotification(
        hasError
          ? `${successCount} integração(ões) enviada(s). ${errorCount} falharam.`
          : "Busca realizada com sucesso",
        {
          type: hasError ? "warning" : "success",
          timeout: 3000
        }
      );
    } else {
      notification.showNotification(
        multipleResult?.Erros?.[0]?.Error ??
          errorResponses[0]?.Error ??
          "Erro ao buscar novamente",
        {
          type: "error",
          timeout: 3000
        }
      );
    }
  } catch {
    sendSearchAgain(
      integrationName,
      AnalyticsIntegracoesResult.Error,
      searchDates
    );
    isLoading.value = false;
  } finally {
    isLoading.value = false;
  }
};

const handleAbrirHistorico = () => {
  if (!showProcessamentoAsync.value?.serviceId) return;
  sendOpenHistory(
    getIntegrationName(),
    AnalyticsIntegracoesHistoryOrigin.SearchAgain
  );
  showHistorico.value = true;
};

const handleApiSelecionada = () => {
  if (usesUnifiedSelection.value) {
    apiSelecionada.value = multipleSearchApiRules.value;
    formularioDinamico.value = markRaw(FormularioIntegracaoBase);

    return;
  }

  const apiRules = handleAPI(
    props.webservice?.API_Hash ?? "",
    props.webservice?.integracao?.toString() ??
      props.webservice?.Webservice?.toString()
  );

  apiSelecionada.value = apiRules;

  formularioDinamico.value = markRaw(
    apiRules?.form ?? FormularioIntegracaoBase
  );
};

const buildChamarServicoParams = (
  selectedWebservice: TWebservice,
  formValues: Record<string, unknown>,
  empresaId?: string
): TChamarServicoParams => {
  const apiRules = handleAPI(
    selectedWebservice.API_Hash ?? "",
    selectedWebservice.integracao?.toString() ??
      selectedWebservice.Webservice?.toString()
  );

  const params: TChamarServicoParams = {
    serviceId: getWebserviceId(selectedWebservice),
    apiHash:
      apiRules?.apiHash ??
      (selectedWebservice.API_Hash as unknown as EIntegracoesAPIEnum),
    apiName:
      selectedWebservice.integracao?.toString() ??
      selectedWebservice.Webservice?.toString(),
    ...formValues
  };

  if (empresaId) {
    params.empresa = empresaId;
    params.empresaId = empresaId;
  }

  if (isMultipleMode.value) {
    params.selecionarPeriodo = true;
    params.PluggyByRange = true;
    params.DataInicio = (formValues.inicio ?? formValues.DataInicio) as
      | string
      | Date
      | null;
    params.DataFim = (formValues.fim ?? formValues.DataFim) as
      | string
      | Date
      | null;
  }

  return params;
};

const buildChamarServicoParamsSelecionados = (
  formValues: Record<string, unknown>
): TChamarServicoParams => {
  const {
    serviceId: _serviceId,
    apiHash: _apiHash,
    apiName: _apiName,
    empresa: _empresa,
    empresaId: _empresaId,
    ...params
  } = formValues as TChamarServicoParams;

  const selectedParams = {
    ...params,
    selecionarPeriodo: true,
    DataInicio: (formValues.inicio ?? formValues.DataInicio) as
      | string
      | Date
      | null,
    DataFim: (formValues.fim ?? formValues.DataFim) as string | Date | null
  };

  return {
    ...selectedParams,
    integrations: selectedWebservices.value.map(getSelectedIntegration)
  };
};

const chamarServicoParaWebservice = async (
  selectedWebservice: TWebservice,
  formValues: Record<string, unknown>
) => {
  const apiRules = handleAPI(
    selectedWebservice.API_Hash ?? "",
    selectedWebservice.integracao?.toString() ??
      selectedWebservice.Webservice?.toString()
  );

  let empresaId: string | undefined;

  if (isMultipleMode.value && apiRules?.requiresCompanyLookup) {
    const response = await financasFeatureGateway.obterEmpresaPorConfiguracao(
      selectedWebservice.id ?? selectedWebservice._id
    );

    if (response?.Ok === false) {
      return {
        Result: false,
        Error: response.Error ?? "Erro ao obter empresa da integração",
        Ok: false
      };
    }

    empresaId = response?.Result;
  }

  return chamarServico(
    buildChamarServicoParams(selectedWebservice, formValues, empresaId)
  );
};

const chamarServicoParaWebservicesSelecionados = async (
  formValues: Record<string, unknown>
) => {
  if (!selectedWebservices.value.length) {
    return {
      Result: false,
      Error: "Nenhuma integração selecionada",
      Ok: false
    };
  }

  return chamarServico(buildChamarServicoParamsSelecionados(formValues));
};

onMounted(async () => {
  handleApiSelecionada();

  const shouldLoadCompanyOptions =
    !isMultipleMode.value &&
    (apiSelecionada.value?.requiresCompanyOptions === true ||
      apiSelecionada.value?.drawerMode !== "informative" &&
        apiSelecionada.value?.hideCompanyField !== true);

  if (shouldLoadCompanyOptions) {
    empresasOpt.value = (await handleEmpresas()) || [];
  }

  if (
    apiSelecionada.value?.bannerRequiresMetaDataRede &&
    props.webservice?.id
  ) {
    const wsResponse = await cadastroGateway.getWebservice(props.webservice.id);
    const metaDataRede = wsResponse?.Result?.MetaDataRede as
      | Record<string, unknown>
      | undefined;
    canShowRedeBanner.value =
      !!metaDataRede && !metaDataRede.HasSuccededRequest;
  }
});
</script>

<style lang="scss" scoped>
.chamarServico-dialog {
  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 0;
  }

  &__content-processamento {
    margin-top: 16px;
    padding: 12px 16px;
    border: 1px solid var(--financas-color-info-light-7);
    border-radius: 8px;
    background-color: var(--financas-color-info-light-9);
  }

  &__content-processamento-title {
    margin: 0 0 4px;
    color: var(--financas-text-color-primary);
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
  }

  &__content-processamento-description {
    margin: 0;
    color: var(--financas-text-color-secondary);
    font-size: 12px;
    line-height: 18px;
  }

  &__content-processamento-description + &__content-processamento-description {
    margin-top: 4px;
  }

  &__content-processamento-highlight {
    color: var(--financas-text-color-primary);
    font-weight: 600;
  }
}
</style>
